import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listEnSubscribers,
  getEnSubscriberStats,
  listEnContentForNewsletter,
  previewEnNewsletter,
  sendEnNewsletter,
} from "@/lib/newsletter-en.functions";
import { addSubscriber, deleteSubscriber } from "@/lib/newsletter.functions";

export const Route = createFileRoute("/admin/newsletter-en")({
  component: NewsletterEnPage,
});

const EMAIL_RE = /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/;

function NewsletterEnPage() {
  const statsFn = useServerFn(getEnSubscriberStats);
  const contentFn = useServerFn(listEnContentForNewsletter);
  const previewFn = useServerFn(previewEnNewsletter);
  const sendFn = useServerFn(sendEnNewsletter);

  const { data: stats } = useQuery({
    queryKey: ["en-newsletter-stats"],
    queryFn: () => statsFn(),
  });
  const { data: contentData, isLoading } = useQuery({
    queryKey: ["en-newsletter-content"],
    queryFn: () => contentFn(),
  });

  const [subject, setSubject] = useState("The Signal Briefing — This Month");
  const [introHtml, setIntroHtml] = useState(
    "<p>A short editorial note on what changed in the debate this month.</p>"
  );
  const [senderName, setSenderName] = useState("Arda Öztaşkın");
  const [testEmail, setTestEmail] = useState("");
  const [ideaSlugs, setIdeaSlugs] = useState<string[]>([]);
  const [conceptSlug, setConceptSlug] = useState<string>("");
  const [researchSlug, setResearchSlug] = useState<string>("");
  const [frameworkSlug, setFrameworkSlug] = useState<string>("");

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [result, setResult] = useState<{ sent: number; failed: number; total: number; errors: string[] } | null>(null);

  const orderedIdeas = useMemo(
    () => ideaSlugs.map((s) => contentData?.ideas.find((i) => i.slug === s)).filter(Boolean),
    [ideaSlugs, contentData]
  );

  const toggleIdea = (slug: string) => {
    setIdeaSlugs((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };
  const moveIdea = (slug: string, dir: -1 | 1) => {
    setIdeaSlugs((prev) => {
      const i = prev.indexOf(slug);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const payload = () => ({
    subject,
    introHtml,
    ideaSlugs,
    conceptSlug: conceptSlug || null,
    researchSlug: researchSlug || null,
    frameworkSlug: frameworkSlug || null,
  });

  const previewMut = useMutation({
    mutationFn: () => previewFn({ data: payload() }),
    onSuccess: (r) => setPreviewHtml(r.html),
  });

  const sendMut = useMutation({
    mutationFn: (opts: { test: boolean }) =>
      sendFn({
        data: {
          ...payload(),
          senderName,
          testEmail: opts.test ? testEmail : undefined,
        },
      }),
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => alert("Send error: " + e.message),
  });

  const canSend = subject.trim() && senderName.trim() && ideaSlugs.length > 0;

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-serif text-3xl">The Signal Briefing</h1>
          <p className="text-xs text-ink/60 mt-1">
            English-language newsletter — sent only to <code>lang=en</code> subscribers.
          </p>
        </div>
        <div className="text-xs text-ink/85">
          {stats ? `${stats.count} EN subscribers` : "Loading…"}
        </div>
      </div>

      <section className="grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Subject line</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Intro (HTML)</label>
            <textarea
              value={introHtml}
              onChange={(e) => setIntroHtml(e.target.value)}
              rows={4}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper font-mono"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Sender name</label>
            <input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            />
            <p className="text-[11px] text-ink/60 mt-1">
              Sent from <code>signal@akillisurdurulebilirlik.com</code> on the verified domain.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Concept in Focus (optional)</label>
            <select
              value={conceptSlug}
              onChange={(e) => setConceptSlug(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            >
              <option value="">— None —</option>
              {(contentData?.concepts ?? []).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Source Note (optional)</label>
            <select
              value={researchSlug}
              onChange={(e) => setResearchSlug(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            >
              <option value="">— None —</option>
              {(contentData?.research ?? []).map((r) => (
                <option key={r.slug} value={r.slug}>{r.title} — {r.institution} ({r.year})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Framework (optional)</label>
            <select
              value={frameworkSlug}
              onChange={(e) => setFrameworkSlug(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            >
              <option value="">— None —</option>
              {(contentData?.frameworks ?? []).map((f) => (
                <option key={f.slug} value={f.slug}>{f.name}</option>
              ))}
            </select>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">
              Selected essays ({ideaSlugs.length})
            </h3>
            {ideaSlugs.length === 0 ? (
              <p className="text-xs text-ink/50">Pick essays on the right.</p>
            ) : (
              <ol className="space-y-1.5">
                {orderedIdeas.map((p, i) => (
                  <li key={p!.slug} className="flex items-center gap-2 text-sm border border-border rounded px-2 py-1.5">
                    <span className="text-xs text-ink/50 w-5">{i + 1}.</span>
                    <span className="flex-1 truncate">{p!.title}</span>
                    <button onClick={() => moveIdea(p!.slug, -1)} className="text-xs text-ink/60 hover:text-ink">↑</button>
                    <button onClick={() => moveIdea(p!.slug, 1)} className="text-xs text-ink/60 hover:text-ink">↓</button>
                    <button onClick={() => toggleIdea(p!.slug)} className="text-xs text-clay">×</button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">Essays (ideas)</h3>
          {isLoading ? (
            <p className="text-sm text-ink/60">Loading…</p>
          ) : (
            <ul className="space-y-1 max-h-[520px] overflow-y-auto border border-border rounded p-2">
              {(contentData?.ideas ?? []).map((p) => {
                const checked = ideaSlugs.includes(p.slug);
                return (
                  <li key={p.slug}>
                    <label className={`flex items-start gap-2 px-2 py-2 rounded cursor-pointer hover:bg-ink/5 ${checked ? "bg-clay/10" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleIdea(p.slug)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-ink/85">
                          {p.topics.join(" · ")} · {p.readingMinutes} min
                        </div>
                        <div className="text-sm">{p.title}</div>
                        <div className="text-xs text-ink/60 italic">{p.subtitle}</div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="border-t border-border pt-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={() => previewMut.mutate()}
            disabled={previewMut.isPending || ideaSlugs.length === 0}
            className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
          >
            {previewMut.isPending ? "…" : "Preview"}
          </button>

          <div className="flex items-center gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@yourdomain.com"
              className="border border-border rounded px-3 py-1.5 text-sm bg-paper w-56"
            />
            <button
              onClick={() => sendMut.mutate({ test: true })}
              disabled={!canSend || !testEmail || sendMut.isPending}
              className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
            >
              Send test
            </button>
          </div>

          <button
            onClick={() => {
              if (!confirm(`Send to ${stats?.count ?? "?"} English subscribers. Continue?`)) return;
              sendMut.mutate({ test: false });
            }}
            disabled={!canSend || sendMut.isPending || (stats?.count ?? 0) === 0}
            className="px-5 py-2 bg-clay text-paper rounded-full text-xs uppercase tracking-[0.22em] disabled:opacity-40 ml-auto"
          >
            {sendMut.isPending ? "Sending…" : `Send to EN subscribers (${stats?.count ?? 0})`}
          </button>
        </div>

        {result && (
          <div className="text-sm border border-border rounded p-4 bg-paper">
            <div>
              <strong>{result.sent}</strong> queued · <strong>{result.failed}</strong> failed · total {result.total}
            </div>
            {result.errors.length > 0 && (
              <ul className="mt-2 text-xs text-clay space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {previewHtml && (
        <section>
          <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">Preview</h3>
          <iframe
            srcDoc={previewHtml}
            className="w-full h-[700px] border border-border rounded bg-white"
            title="EN newsletter preview"
          />
        </section>
      )}

      <EnSubscribersPanel />
    </div>
  );
}

function EnSubscribersPanel() {
  const listFn = useServerFn(listEnSubscribers);
  const addFn = useServerFn(addSubscriber);
  const delFn = useServerFn(deleteSubscriber);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["en-newsletter-subscribers"],
    queryFn: () => listFn(),
  });

  const [newEmail, setNewEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const addMut = useMutation({
    mutationFn: async () => {
      const email = newEmail.trim().toLowerCase();
      if (!EMAIL_RE.test(email)) throw new Error("Invalid email");
      return addFn({ data: { email, lang: "en" } });
    },
    onSuccess: (res: any) => {
      if (res?.already) setMsg("Already subscribed.");
      else {
        setMsg("Added.");
        setNewEmail("");
      }
      refetch();
    },
    onError: (e: any) => setMsg(e.message || "Error"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => delFn({ data: { id } }),
    onSuccess: () => refetch(),
  });

  const subs = (data?.subscribers ?? []).filter((s: any) =>
    filter ? s.email.toLowerCase().includes(filter.toLowerCase()) : true
  );

  return (
    <section className="border-t border-border pt-8 space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif text-2xl">English subscribers</h2>
        <div className="text-xs text-ink/85">{data?.subscribers?.length ?? 0} records</div>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-2 items-end">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Email</label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="reader@domain.com"
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setMsg(null);
            addMut.mutate();
          }}
          disabled={addMut.isPending}
          className="bg-ink text-paper text-xs uppercase tracking-[0.18em] px-4 py-2 rounded disabled:opacity-60"
        >
          {addMut.isPending ? "Adding…" : "Add manually"}
        </button>
      </div>
      {msg && <p className="text-xs text-ink/70">{msg}</p>}

      <div>
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by email"
          className="w-full md:w-72 border border-border rounded px-3 py-2 text-sm bg-paper mb-3"
        />
        <div className="border border-border rounded max-h-[480px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink/5 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium w-40">Subscribed</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={3} className="px-3 py-4 text-ink/60">Loading…</td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={3} className="px-3 py-4 text-ink/60">No records.</td></tr>
              ) : (
                subs.map((s: any) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-3 py-2 break-all">{s.email}</td>
                    <td className="px-3 py-2 text-ink/85">
                      {new Date(s.subscribed_at).toLocaleDateString("en-US")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete ${s.email}?`)) delMut.mutate(s.id);
                        }}
                        className="text-xs text-clay hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
